import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MainLayout from './MainLayout';
import styles from '../../assets/styles/components/ReportLayout.module.scss';
import { useReportFilter, DatePeriodKey, ComparisonPeriodKey } from '../../contexts/ReportContext';

interface ReportLayoutProps {
  children: React.ReactNode;
  title?: string;
  showTabs?: boolean;
  showGlobalFilters?: boolean;
}

const ReportLayout: React.FC<ReportLayoutProps> = ({ children, title = "分析レポート", showTabs = true, showGlobalFilters = true }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    selectedPeriod, setSelectedPeriod,
    customStart, setCustomStart,
    customEnd, setCustomEnd,
    showComparison, setShowComparison,
    comparisonPeriod, setComparisonPeriod
  } = useReportFilter();

  // Define tabs configuration
  const tabs = [
    { label: '広告分析', path: '/report' },
    { label: 'サイト分析', path: '/report/site' },
    { label: '検索広告', path: '/report/search' },
    { label: 'クリエイティブ', path: '/report/creative' },
    { label: 'データテーブル', path: '/report/data' },
    { label: 'レポート出力', path: '/report/export' },
  ];

  // Helper to determine if a tab is active
  const isActive = (path: string) => {
    if (path === '/report') {
      return location.pathname === '/report';
    }
    return location.pathname.startsWith(path);
  };

  const headerContent = showTabs ? (
    <nav className={styles.nav}>
      {tabs.map((tab) => {
        const active = isActive(tab.path);
        return (
          <button
            key={tab.path}
            onClick={() => navigate(tab.path)}
            className={`${styles.tabButton} ${active ? styles.active : ''}`}
          >
            {tab.label}
          </button>
        );
      })}
    </nav>
  ) : null;

  const headerRight = (
    <div className={styles.headerRight}>
      {/* Date Filter & Comparison Area */}
      {showGlobalFilters && (
        <div className={styles.filterArea}>

          {/* Comparison Controls */}
          {showComparison ? (
            <div className={styles.comparisonControl}>
              <span className={styles.vsLabel}>VS</span>
              <select
                value={comparisonPeriod}
                onChange={(e) => setComparisonPeriod(e.target.value as ComparisonPeriodKey)}
                className={styles.comparisonSelect}
              >
                {/* optionのvalueをキーに変更 */}
                <option value="last_month">先月</option>
                <option value="last_year_same_month">前年同月</option>
                <option value="custom">カスタム</option>
              </select>
              {/* ... (閉じるボタン等はそのまま) ... */}
            </div>
          ) : (
            <button
              onClick={() => setShowComparison(true)}
              className={styles.addComparisonButton}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              比較対象を追加
            </button>
          )}

          {/* Main Period Selector */}
          <div className={styles.periodSelector}>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value as DatePeriodKey)}
              className={styles.periodSelect}
            >
              {/* optionのvalueをキーに変更 */}
              <option value="last_30_days">直近30日</option>
              <option value="this_month">今月</option>
              <option value="last_month">先月</option>
              <option value="this_week">今週</option>
              <option value="custom">カスタム</option>
            </select>
          </div>

          {/* Custom Date Range Inputs (Conditional) */}
          {selectedPeriod === 'custom' && (
            <div className={styles.customDateRange}>
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className={styles.dateInput} />
              <span className={styles.separator}>-</span>
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className={styles.dateInput} />
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <MainLayout title={title} headerRight={headerRight} headerContent={headerContent}>
      {children}
    </MainLayout>
  );
};

export default ReportLayout;
